import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	useFloating,
	autoUpdate,
	offset,
	flip,
	shift,
	size,
	FloatingPortal,
} from '@floating-ui/react';
/* eslint-disable import/no-unresolved */
import { Saturation, Hue, EditableInput, EditableInputRGBA, Swatch, hexToHsva, hsvaToHex, type ColorResult, type SwatchRectRenderProps } from '@uiw/react-color';
import { BsEyedropper } from 'react-icons/bs';

import BayPillToggle from '../BayPillToggle';
import './BayColorPicker.scss';

interface HsvaColor {
	h: number;
	s: number;
	v: number;
	a: number;
}

export interface BrandColor {
	/** Human label shown in the tooltip / radio row, e.g. "Brand Primary". */
	label: string;
	/** The actual color to display in the swatch (resolved hex). */
	color: string;
	/** The value emitted on select — a merge tag like "{{user.brandPrimaryColor}}". */
	value: string;
	/** Optional descriptive line shown under the label in Dynamic mode. */
	description?: string;
}

export interface BayColorPickerProps {
	value?: string;
	onChange?: (_val: string) => void;
	onCommit?: (_val: string) => void;
	onOpenChange?: (_isOpen: boolean) => void;
	children?: React.ReactNode;
	showInput?: boolean;
	/** When true, show the Alpha (A) channel input and allow transparent colors. Defaults to false. */
	showAlpha?: boolean;
	presetColors?: string[];
	/** Brand swatches that apply a dynamic merge tag instead of a literal hex. */
	brandColors?: BrandColor[];
	/** When false, hide the BRAND swatches section even if brandColors are provided. Defaults to true. */
	showBrandColors?: boolean;
	/**
	 * CAPABILITY gate: does this surface support dynamic brand colors at all? Set
	 * where dynamic vars make sense (it stays false where they don't). This alone
	 * no longer shows any Dynamic UI — the Static/Dynamic toggle and dynamic panel
	 * appear only when BOTH `supportsDynamic` and `enableDynamicMode` are true.
	 */
	supportsDynamic?: boolean;
	/**
	 * VISIBILITY gate: even where Dynamic Mode is supported, should its UI show?
	 * Kept separate from `supportsDynamic` so the whole feature can be hidden per
	 * instance without touching callers. Defaults
	 * to `false`, so Dynamic Mode is OFF everywhere unless a consumer explicitly
	 * opts in — the product-wide hide from ticket 13020733812. Dynamic Mode's code
	 * is fully retained; pass `enableDynamicMode` (with `supportsDynamic`) to bring
	 * it back. Both must be true to show the toggle/panel.
	 */
	enableDynamicMode?: boolean;
	/**
	 * Called when a dynamic brand color is selected, with the brand merge tag and the
	 * brand's resolved hex. When provided, this takes precedence over the default
	 * onChange/onCommit(tag) — used by the RTE, which applies the tag to the selection
	 * via execCommand (using the hex for the visible color) and builds its own badge.
	 */
	onDynamicCommit?: (_brandValue: string, _brandHex: string) => void;
	/**
	 * Optional copy overrides for the dynamic brand-color panel. When omitted, the
	 * default (template-author) wording is used; callers like the BAY admin screen
	 * pass their own audience-specific copy. Parts that interpolate the previewed
	 * brand accept a function of (brandLabel, brandHex).
	 */
	dynamicCopy?: DynamicCopy;
	customColorLabel?: string;
	className?: string;
	getPopupContainer?: () => HTMLElement;
}

export interface DynamicCopy {
	heading?: string;
	subcopy?: string;
	explainerTitle?: string;
	explainerBody?: string;
	/** Note under the "Your Current View" preview. (brandLabel, brandHex) => string. */
	previewNote?: (_brandLabel: string, _brandHex: string) => string;
}

const DEFAULT_DYNAMIC_COPY: Required<DynamicCopy> = {
	heading: 'Dynamic Colors',
	subcopy:
		'Build templates that brand themselves. Link this element to a dynamic color below, and it will automatically update to match each user\'s unique colors when they use it.',
	explainerTitle: '💡 How will others see this?',
	explainerBody:
		'When your users view this template in the grid or open it in the editor, it will already be loaded with their own colors. No manual edits required.',
	previewNote: (brandLabel) =>
		`You are currently seeing your brand’s ${brandLabel} color as a placeholder to help you design.`,
};

/** Copy shown to BAY global-admin roles (authoring templates for their users). */
export const ADMIN_DYNAMIC_COPY: Required<DynamicCopy> = {
	heading: 'Dynamic Colors',
	subcopy:
		'Build templates that brand themselves. Link this element to a dynamic color below, and it will automatically update to match each company\'s unique colors when they use it.',
	explainerTitle: '💡 How will others see this?',
	explainerBody:
		'When a company views this template in their grid or opens it in the editor, it will already be loaded with their brand colors. No manual edits required.',
	previewNote: (brandLabel) =>
		`You are currently seeing Back At You’s ${brandLabel} color as a placeholder to help you design.`,
};

const CHECKERBOARD_BACKGROUND =
	'linear-gradient(45deg, #d9d9d9 25%, transparent 25%), linear-gradient(-45deg, #d9d9d9 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d9d9d9 75%), linear-gradient(-45deg, transparent 75%, #d9d9d9 75%)';
const HEX_COLOR_PATTERN = /^#?[0-9a-fA-F]{6}$/;
const POPUP_SPACING_PX = 8;

const CHANNEL_LABEL_STYLE: React.CSSProperties = {
	fontSize: '0.625rem',
	fontWeight: 600,
	color: 'var(--bs-secondary-color, #6c757d)',
	textTransform: 'uppercase',
	textAlign: 'center',
	lineHeight: 1,
	marginTop: 0,
};

const clamp255 = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
const toHexPair = (n: number) => clamp255(n).toString(16).padStart(2, '0');

// Convert a hex (#rgb / #rrggbb / #rrggbbaa) or an rgb()/rgba() string to #RRGGBB.
// getComputedStyle reports colors as "rgb(r, g, b)", which is why rgb parsing is needed.
const anyColorToHex = (value: string): string => {
	const v = value.trim();
	const rgbMatch = v.match(/^rgba?\(([^)]+)\)$/i);
	if (rgbMatch) {
		const [r, g, b] = rgbMatch[1].split(',').map((part) => parseFloat(part));
		return `#${toHexPair(r)}${toHexPair(g)}${toHexPair(b)}`.toUpperCase();
	}
	let hex = v.startsWith('#') ? v.slice(1) : v;
	if (hex.length === 3) {
		hex = hex.split('').map((c) => c + c).join('');
	}
	return `#${hex.slice(0, 6).padEnd(6, '0')}`.toUpperCase();
};

const toNormalizedColor = (nextValue: string) => {
	const trimmedValue = nextValue.trim();
	if (!trimmedValue) return '';
	if (trimmedValue.toLowerCase() === 'transparent') return 'Transparent';

	if (!HEX_COLOR_PATTERN.test(trimmedValue)) return null;

	const prefixedValue = trimmedValue.startsWith('#')
		? trimmedValue
		: `#${trimmedValue}`;

	return prefixedValue.toUpperCase();
};

const normalizeValueForPicker = (value?: string) => {
	if (!value) return '';
	if (value.toLowerCase() === 'transparent') return 'Transparent';

	try {
		return anyColorToHex(value);
	} catch {
		return value;
	}
};

const isTransparentColor = (value?: string) => {
	if (!value) return false;
	const v = value.trim();
	if (v.toLowerCase() === 'transparent') return true;

	const rgbaMatch = v.match(/^rgba?\(([^)]+)\)$/i);
	if (rgbaMatch) {
		const parts = rgbaMatch[1].split(',').map((part) => parseFloat(part));
		return parts.length === 4 && parts[3] === 0;
	}

	// 8-digit hex with a 00 alpha channel.
	return /^#[0-9a-f]{8}$/i.test(v) && v.slice(-2).toLowerCase() === '00';
};

const toHsvaColor = (value: string): HsvaColor => {
	const hsva = hexToHsva(anyColorToHex(value));
	return {
		h: Number.isFinite(hsva.h) ? hsva.h : 0,
		s: Number.isFinite(hsva.s) ? hsva.s : 0,
		v: Number.isFinite(hsva.v) ? hsva.v : 100,
		a: 1,
	};
};

const BayColorPicker: React.FC<BayColorPickerProps> = ({
	value = '',
	onChange,
	onCommit,
	onOpenChange,
	children,
	showInput = !children,
	showAlpha = false,
	presetColors = [],
	brandColors = [],
	showBrandColors = true,
	supportsDynamic = false,
	enableDynamicMode = false,
	onDynamicCommit,
	dynamicCopy,
	className,
	getPopupContainer,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [draftColor, setDraftColor] = useState('');
	const [hsva, setHsva] = useState<HsvaColor>(() => ({ h: 0, s: 0, v: 100, a: 1 }));

	const triggerRef = useRef<HTMLDivElement>(null);
	const pickerRef = useRef<HTMLDivElement>(null);
	const pendingCommitColorRef = useRef<string | null>(null);

	// Dynamic Mode shows only when a caller opts in on BOTH gates. `enableDynamicMode`
	// defaults false, so it is hidden product-wide (ticket 13020733812) until a
	// consumer explicitly re-enables it per instance.
	const dynamicEnabled = supportsDynamic && enableDynamicMode;

	// Floating UI handles edge management: flips above when there's no room below,
	// shifts to stay on-screen, and caps the height to the viewport (with scroll) so
	// the taller Dynamic panel never overflows. autoUpdate recomputes on the mode
	// switch (height change), scroll, and resize.
	const { refs, floatingStyles } = useFloating<HTMLDivElement>({
		open: isOpen,
		placement: 'bottom-start',
		strategy: 'fixed',
		whileElementsMounted: autoUpdate,
		middleware: [
			offset(POPUP_SPACING_PX),
			flip({ padding: POPUP_SPACING_PX }),
			shift({ padding: POPUP_SPACING_PX }),
			size({
				padding: POPUP_SPACING_PX,
				apply({ availableHeight, elements }) {
					Object.assign(elements.floating.style, {
						maxHeight: `${Math.max(180, availableHeight)}px`,
					});
				},
			}),
		],
	});

	// Bridge Floating UI's reference/floating refs with our own refs (used for the
	// click-outside check), so a single DOM node feeds both.
	const setTriggerRef = useCallback((node: HTMLDivElement | null) => {
		triggerRef.current = node;
		refs.setReference(node);
	}, [refs]);
	const setFloatingRef = useCallback((node: HTMLDivElement | null) => {
		pickerRef.current = node;
		refs.setFloating(node);
	}, [refs]);

	// A dynamic value is simply one of the brand merge tags (e.g. "{{user.brandPrimaryColor}}").
	// No fallback — every customer has brand colors, so the bare tag resolves at send time.
	const activeBrand = useMemo(
		() => (supportsDynamic
			? brandColors.find((b) => b.value === value) || null
			: null),
		[supportsDynamic, brandColors, value],
	);
	const isDynamicValue = activeBrand !== null;

	// Picker mode. Seeded from the value, but user-toggleable while open.
	const [mode, setMode] = useState<'static' | 'dynamic'>(
		isDynamicValue ? 'dynamic' : 'static',
	);

	// Which brand is selected in the dynamic panel (defaults to the active one).
	const [selectedBrandValue, setSelectedBrandValue] = useState<string | null>(
		activeBrand?.value ?? null,
	);

	// In dynamic mode the underlying `value` is a merge tag, so the static picker
	// displays the selected brand's resolved hex instead.
	const selectedBrand = useMemo(
		() => brandColors.find((b) => b.value === selectedBrandValue) || null,
		[brandColors, selectedBrandValue],
	);
	const staticDisplayValue = isDynamicValue
		? (activeBrand?.color || '')
		: value;
	const normalizedValue = useMemo(
		() => normalizeValueForPicker(staticDisplayValue),
		[staticDisplayValue],
	);
	const isTransparent = useMemo(
		() => isTransparentColor(normalizedValue),
		[normalizedValue],
	);

	// The native EyeDropper API only exists in a secure context (HTTPS / localhost).
	const eyeDropperSupported =
		typeof window !== 'undefined' && 'EyeDropper' in window;

	const notifyOpenChange = useCallback(
		(nextOpen: boolean) => {
			onOpenChange?.(nextOpen);
		},
		[onOpenChange],
	);

	const flushPendingCommit = useCallback(() => {
		if (!pendingCommitColorRef.current) return;
		onCommit?.(pendingCommitColorRef.current);
		pendingCommitColorRef.current = null;
	}, [onCommit]);

	const closePicker = useCallback(
		(shouldCommit = true) => {
			if (shouldCommit) {
				flushPendingCommit();
			}
			setIsOpen(false);
			notifyOpenChange(false);
		},
		[flushPendingCommit, notifyOpenChange],
	);

	const applyColor = useCallback(
		(nextColor: string, shouldClose = false, commitImmediately = false) => {
			const normalizedColor = toNormalizedColor(nextColor);
			if (!normalizedColor) return false;

			// Picking a static color replaces any dynamic brand selection — deselect the
			// brand radio so the element is no longer treated as dynamic.
			setSelectedBrandValue(null);

			onChange?.(normalizedColor);

			if (normalizedColor.toLowerCase() !== 'transparent') {
				try {
					setHsva(toHsvaColor(normalizedColor));
				} catch {
					// Ignore invalid conversion values.
				}
			}

			if (commitImmediately) {
				onCommit?.(normalizedColor);
				pendingCommitColorRef.current = null;
			} else {
				pendingCommitColorRef.current = normalizedColor;
			}

			setDraftColor(normalizedColor);

			if (shouldClose) {
				closePicker(false);
			}

			return true;
		},
		[closePicker, onChange, onCommit],
	);

	const commitDraftColor = useCallback(
		(nextValue: string) => {
			const wasApplied = applyColor(nextValue, false, true);
			if (!wasApplied) {
				setDraftColor(normalizedValue || '');
			}
		},
		[applyColor, normalizedValue],
	);

	// Select a dynamic brand color. Stores the bare merge tag (e.g.
	// "{{user.brandPrimaryColor}}") — no fallback, since every customer has brand
	// colors that resolve at preview/send time. The popover stays open so the
	// "Your Current View" preview reflects the live selection (per the design).
	const selectBrandColor = useCallback(
		(brand: BrandColor, shouldClose = false) => {
			setSelectedBrandValue(brand.value);

			if (onDynamicCommit) {
				// The RTE applies the tag to the selection via execCommand and builds its
				// own badge; it needs the brand's resolved hex for the visible color.
				onDynamicCommit(brand.value, brand.color);
			} else {
				onChange?.(brand.value);
				onCommit?.(brand.value);
			}
			pendingCommitColorRef.current = null;

			if (shouldClose) {
				closePicker(false);
			}
		},
		[onDynamicCommit, onChange, onCommit, closePicker],
	);

	const handleToggle = useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			if (!triggerRef.current) return;

			if (isOpen) {
				closePicker(true);
				return;
			}

			// Seed mode + selected brand from the current value on open. Only reflect a
			// brand that is actually applied — when nothing is set yet, both radios stay
			// unchecked until the user picks one (no auto-selected default).
			setMode(isDynamicValue ? 'dynamic' : 'static');
			setSelectedBrandValue(activeBrand?.value ?? null);

			// Snapshot hsva from the current value when opening so the picker
			// starts at the right position. We can't rely on the useEffect for
			// this because it is blocked while the picker is open.
			if (normalizedValue && normalizedValue.toLowerCase() !== 'transparent') {
				try { setHsva(toHsvaColor(normalizedValue)); } catch { /* ignore */ }
			}

			setIsOpen(true);
			notifyOpenChange(true);
		},
		[closePicker, isOpen, isDynamicValue, activeBrand, brandColors, normalizedValue, notifyOpenChange],
	);

	const handleDraftKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			event.stopPropagation();
			if (event.key !== 'Enter') return;
			event.preventDefault();
			commitDraftColor(draftColor);
		},
		[commitDraftColor, draftColor],
	);

	const handleUiwColorChange = useCallback(
		(result: HsvaColor) => {
			const h = Number.isFinite(result.h) ? result.h : 0;
			const s = Number.isFinite(result.s) ? result.s : 0;
			const v = Number.isFinite(result.v) ? result.v : 100;
			const a = Number.isFinite(result.a) ? result.a : 1;
			try {
				const hex = hsvaToHex({ h, s, v, a: 1 });
				applyColor(hex);
			} catch {
				// ignore invalid conversions
			}
			// Set hsva after applyColor so our precise drag values override the toHsvaColor round-trip inside applyColor
			setHsva({ h, s, v, a });
		},
		[applyColor],
	);

	// Hue onChange only passes { h } — s/v/a must come from current hsva state
	const handleHueChange = useCallback(
		({ h }: { h: number }) => {
			const { s, v, a } = hsva;
			try {
				const hex = hsvaToHex({ h, s, v, a: 1 });
				applyColor(hex);
			} catch {
				// ignore invalid conversions
			}
			setHsva({ h, s, v, a });
		},
		[hsva, applyColor],
	);

	const renderChannelInput = useCallback(
		(
			inputProps: React.InputHTMLAttributes<HTMLInputElement>,
			ref: React.Ref<HTMLInputElement>,
		) => (
			<input
				{...inputProps}
				ref={ref as React.RefObject<HTMLInputElement>}
				className="form-control form-control-sm bay-color-picker__channel-input"
			/>
		),
		[],
	);

	const handleHexLibChange = useCallback(
		(_evn: React.ChangeEvent<HTMLInputElement>, val: string | number) => {
			if (typeof val === 'string') {
				applyColor(`#${val}`);
			}
		},
		[applyColor],
	);

	const handleRgbaLibChange = useCallback(
		(colorResult: ColorResult) => {
			applyColor(`#${colorResult.hex}`);
			setHsva({ ...colorResult.hsva });
		},
		[applyColor],
	);

	const handleEyeDropper = useCallback(async () => {
		const EyeDropperCtor = (window as unknown as { EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> } }).EyeDropper;
		if (!EyeDropperCtor) return;
		try {
			const result = await new EyeDropperCtor().open();
			if (result?.sRGBHex) {
				// Commit immediately, keep the picker open so the user can fine-tune.
				applyColor(result.sRGBHex, false, true);
			}
		} catch {
			// User dismissed the eyedropper (Escape) — nothing to do.
		}
	}, [applyColor]);

	const renderSwatch = useCallback(
		({ color, checked }: SwatchRectRenderProps) => {
			const isTransp = color.toLowerCase() === 'transparent';
			return (
				<button
					type="button"
					title={color}
					className={clsx('bay-color-picker__preset', {
						'bay-color-picker__preset--selected': checked,
						'bay-color-picker__preset--transparent': isTransp,
					})}
					style={{
						backgroundColor: isTransp ? '#ffffff' : color,
						backgroundImage: isTransp ? CHECKERBOARD_BACKGROUND : undefined,
					}}
					onMouseDown={(event) => event.preventDefault()}
					onClick={() => applyColor(isTransp ? 'Transparent' : color, false, true)}
				/>
			);
		},
		[applyColor],
	);

	useEffect(() => {
		// While the picker is open, the parent re-renders on every onChange call
		// (because execCommand changes the text color) and passes a new value prop.
		// Syncing hsva from that value would reset the drag position on every frame.
		// Only sync from external value when the picker is closed.
		if (isOpen) return;

		setDraftColor(normalizedValue || '');
		if (!normalizedValue || normalizedValue.toLowerCase() === 'transparent') return;

		try {
			setHsva(toHsvaColor(normalizedValue));
		} catch {
			// Ignore invalid conversion values.
		}
	}, [isOpen, normalizedValue]);

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			if (
				pickerRef.current?.contains(target) ||
				triggerRef.current?.contains(target)
			) {
				return;
			}

			closePicker(true);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closePicker(true);
			}
		};

		// Repositioning on scroll/resize is handled by Floating UI's autoUpdate.
		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [closePicker, isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const handlePointerUp = () => {
			flushPendingCommit();
		};

		document.addEventListener('pointerup', handlePointerUp);

		return () => {
			document.removeEventListener('pointerup', handlePointerUp);
		};
	}, [flushPendingCommit, isOpen]);

	const popupContainer = getPopupContainer?.() || document.body;
	// The Transparent swatch is a discrete color choice and must always be offered to
	// end users — it is independent of the Alpha (A) channel input, which `showAlpha`
	// controls. Default presets include it; explicit presets are honored as-is.
	const resolvedPresetColors = useMemo(
		() => (presetColors.length
			? presetColors
			: ['#000000', '#ffffff', '#165DFF', 'Transparent']),
		[presetColors],
	);

	const renderStaticPanel = () => (
		<>
			<div className="bay-color-picker__saturation">
				<Saturation hsva={hsva} onChange={handleUiwColorChange} />
			</div>

			<div className="bay-color-picker__hue">
				<Hue hue={hsva.h} onChange={handleHueChange} />
			</div>

			<div className="bay-color-picker__input-row">
				<button
					type="button"
					title={
						eyeDropperSupported
							? 'Pick a color from the screen'
							: 'Eyedropper requires a secure (HTTPS) page'
					}
					aria-label="Pick a color from the screen"
					className="bay-color-picker__eyedropper-btn"
					disabled={!eyeDropperSupported}
					onMouseDown={(event) => event.preventDefault()}
					onClick={handleEyeDropper}
				>
					<BsEyedropper />
				</button>
				<div className="bay-color-picker__input-group bay-color-picker__input-group--hex">
					<span className="bay-color-picker__input-label">Hex</span>
					<EditableInput
						value={draftColor.replace(/^#/, '')}
						placeholder="000000"
						renderInput={renderChannelInput}
						onChange={handleHexLibChange}
						onKeyDown={handleDraftKeyDown}
						style={{ position: 'relative', display: 'block', fontSize: 'inherit' }}
					/>
				</div>
				<EditableInputRGBA
					hsva={hsva}
					placement="top"
					onChange={handleRgbaLibChange}
					rProps={{ renderInput: renderChannelInput, labelStyle: CHANNEL_LABEL_STYLE, className: 'bay-color-picker__input-group', style: { flex: 1, minWidth: '2rem' } }}
					gProps={{ renderInput: renderChannelInput, labelStyle: CHANNEL_LABEL_STYLE, className: 'bay-color-picker__input-group', style: { flex: 1, minWidth: '2rem' } }}
					bProps={{ renderInput: renderChannelInput, labelStyle: CHANNEL_LABEL_STYLE, className: 'bay-color-picker__input-group', style: { flex: 1, minWidth: '2rem' } }}
					aProps={showAlpha ? { renderInput: renderChannelInput, labelStyle: CHANNEL_LABEL_STYLE, className: 'bay-color-picker__input-group', style: { flex: 1, minWidth: '2rem' } } : false}
					style={{ gap: '0.375rem', flex: 1 }}
				/>
			</div>

			{resolvedPresetColors.length > 0 && (
				<>
					<div className="bay-color-picker__presets-label">RECENT</div>
					<Swatch
						className="bay-color-picker__preset-grid"
						color={normalizedValue}
						colors={resolvedPresetColors}
						rectRender={renderSwatch}
						onChange={(_hsvaColor, colorResult) => {
							applyColor(`#${colorResult.hex}`, true, true);
							setHsva({ ..._hsvaColor });
						}}
						style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '0.375rem' }}
					/>
				</>
			)}

			{/* Brand colors are just static swatches here. Applying one sets its
			    resolved color — going dynamic (emitting the merge tag) is the job of
			    the Static/Dynamic toggle, not a swatch click. */}
			{showBrandColors && brandColors.length > 0 && (
				<>
					<div className="bay-color-picker__presets-label bay-color-picker__presets-label--brand">BRAND COLORS</div>
					<div className="bay-color-picker__brand-grid">
						{brandColors.map((brand) => (
							<button
								key={brand.value}
								type="button"
								title={`${brand.label} (${brand.color})`}
								className="bay-color-picker__preset bay-color-picker__brand-swatch"
								style={{ backgroundColor: brand.color }}
								onMouseDown={(event) => event.preventDefault()}
								onClick={() => applyColor(brand.color, false, true)}
							/>
						))}
					</div>
				</>
			)}
		</>
	);

	const renderDynamicPanel = () => {
		// The brand whose color is previewed in "Your Current View" — only the actually
		// selected/applied brand. No default: with nothing chosen there's nothing to preview.
		const previewBrand = selectedBrand || activeBrand || null;
		// Resolved copy: caller overrides on top of the default (template-author) wording.
		const copy = { ...DEFAULT_DYNAMIC_COPY, ...dynamicCopy };

		return (
			<div className="bay-color-picker__dynamic">
				{brandColors.length === 0 ? (
					<div className="bay-color-picker__dynamic-empty">
						No brand colors are available.
					</div>
				) : (
					<>
						<div className="bay-color-picker-explainer-wrapper-upper px-3 mb-3">
							<div className="bay-color-picker__dynamic-heading">{copy.heading}</div>
							<div className="bay-color-picker__dynamic-subcopy">{copy.subcopy}</div>

							<div className="bay-color-picker__brand-options">
								{brandColors.map((brand) => {
									const isSelected = brand.value === selectedBrandValue;
									return (
										<button
											key={brand.value}
											type="button"
											className={clsx('bay-color-picker__brand-card', {
												'bay-color-picker__brand-card--selected': isSelected,
											})}
											onMouseDown={(event) => event.preventDefault()}
											onClick={() => selectBrandColor(brand)}
										>
											<span
												className={clsx('bay-color-picker__radio', {
													'bay-color-picker__radio--checked': isSelected,
												})}
											/>
											<span className="bay-color-picker__brand-card-label">
												{brand.label}
											</span>
										</button>
									);
								})}
							</div>
						</div>

						<div className="bay-color-picker-explainer-wrapper-lower bg-25 p-3 rounded">
							<div className="bay-color-picker__dynamic-explainer-title">
								{copy.explainerTitle}
							</div>
							<div className="bay-color-picker__dynamic-subcopy">
								{copy.explainerBody}
							</div>

							{previewBrand && (
								<>
									<div className="bay-color-picker__dynamic-preview-label">Your Current View</div>
									<div className="bay-color-picker__dynamic-preview">
										<span
											className="bay-color-picker__dynamic-preview-swatch"
											style={{ backgroundColor: previewBrand.color }}
										/>
										<span className="bay-color-picker__dynamic-preview-hex">
											{previewBrand.color.toUpperCase()}
										</span>
									</div>
									<div className="bay-color-picker__dynamic-note">
										{copy.previewNote(previewBrand.label, previewBrand.color)}
									</div>
								</>
							)}
						</div>
					</>
				)}
			</div>
		);
	};

	return (
		<div className={clsx('bay-color-picker', className, { 'bay-color-picker--with-input': showInput })}>
			{(() => {
				const swatch = (isDynamicValue ? activeBrand?.color : normalizedValue) ? (
					<span
						className={clsx('bay-color-picker__swatch', {
							'bay-color-picker__swatch--transparent': isTransparent && !isDynamicValue,
						})}
						style={{
							backgroundColor: isDynamicValue
								? (activeBrand?.color || '#ffffff')
								: isTransparent ? '#ffffff' : normalizedValue,
							backgroundImage: !isDynamicValue && isTransparent ? CHECKERBOARD_BACKGROUND : undefined,
						}}
					/>
				) : (
					<BsEyedropper className="bay-color-picker__eyedropper" />
				);

				// Display-only consolidated field: one box holding the swatch + printed
				// color, plus the brand name (muted) when dynamic. The "{ } Dynamic" pill
				// lives in the field label (above), not here. Clicking opens the popover.
				if (showInput && !children) {
					return (
						<div
							ref={setTriggerRef}
							className="bay-color-picker__trigger bay-color-picker__value-display"
							onMouseDown={(event) => event.preventDefault()}
							onClick={handleToggle}
						>
							<span className="bay-color-picker__value-swatch-shell">{swatch}</span>
							<span className="bay-color-picker__value-text">
								{isDynamicValue
									? (activeBrand?.color || '')
									: (normalizedValue || '')}
							</span>
							{/* The brand-name badge is a "this is a Dynamic color" indicator, so
							    it is gated with the rest of Dynamic Mode (dynamicEnabled,
							    ticket 13020733812). The swatch/value-text still resolve to the
							    brand's hex, so a dynamic value renders as a plain static color. */}
							{dynamicEnabled && isDynamicValue && activeBrand && (
								<span className="bay-color-picker__value-brand">
									{activeBrand.label.toUpperCase()}
								</span>
							)}
						</div>
					);
				}

				// Icon / children-driven usage (e.g. RTE toolbar): just the swatch trigger.
				return (
					<div
						ref={setTriggerRef}
						className="bay-color-picker__trigger"
						onMouseDown={(event) => event.preventDefault()}
						onClick={handleToggle}
					>
						{children || <div className="bay-color-picker__swatch-shell">{swatch}</div>}
					</div>
				);
			})()}

			{isOpen && (
				<FloatingPortal root={popupContainer}>
					<div
						ref={setFloatingRef}
						className="bay-color-picker__popover scrollbar-sm"
						style={floatingStyles}
					>
						{dynamicEnabled && (
							<BayPillToggle<'static' | 'dynamic'>
								className="bay-color-picker__mode-toggle"
								ariaLabel="Color mode"
								fullWidth
								options={[
									{ value: 'static', label: 'Static' },
									{ value: 'dynamic', label: 'Dynamic' },
								]}
								value={mode}
								onChange={(nextMode) => {
									// Only switch which panel is shown. The applied colour and the
									// brand selection are preserved — switching Dynamic → Static does
									// NOT clear anything. A change is only committed when the user
									// actually picks a new colour (static swatch/hex, or a brand radio);
									// blurring out of the panel without picking commits nothing.
									setMode(nextMode);
								}}
							/>
						)}

						{dynamicEnabled && mode === 'dynamic'
							? renderDynamicPanel()
							: renderStaticPanel()}
					</div>
				</FloatingPortal>
			)}
		</div>
	);
};

export default BayColorPicker;
